import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';
import {
  getNotifLocal,
  setNotifLocal,
  parseUserNotifPrefs
} from '../../utils/mobileSettingsStorage';
import './MobileNotificationSettingsPage.css';

const defaultPrefs = () => ({
  systemPush: false,
  channels: { push: true, email: true, inApp: true }
});

export default function MobileNotificationSettingsPage() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [ui, setUi] = useState({
    hero: {
      heading: 'Push notifications',
      description:
        'Enable push messages to receive order updates and promotions. You can disable it at any time by going into the push settings on this page.',
      enableCta: 'Enable',
      topBannerColor: '#fcece0'
    },
    channels: [
      { id: 'push', label: 'Push', value: 'push' },
      { id: 'email', label: 'Email', value: 'email' },
      { id: 'in_app', label: 'In-app messages', value: 'in_app' }
    ]
  });
  const [prefs, setPrefs] = useState(getNotifLocal);

  const loadUi = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/settings/notification-ui/public`);
      if (res.ok) {
        const data = await res.json();
        if (data.hero && data.channels) setUi(data);
      }
    } catch (_) {}
  }, []);

  const mergeUserPrefs = useCallback(() => {
    if (user?.notificationPrefs) {
      const p = parseUserNotifPrefs(user.notificationPrefs);
      if (p) {
        setPrefs((prev) => ({ ...defaultPrefs(), ...prev, ...p }));
        return;
      }
    }
    setPrefs(getNotifLocal());
  }, [user]);

  useEffect(() => {
    loadUi();
  }, [loadUi]);

  useEffect(() => {
    mergeUserPrefs();
  }, [mergeUserPrefs]);

  const persist = async (next) => {
    setNotifLocal(next);
    if (user) {
      await updateProfile({ notificationPrefs: JSON.stringify(next) });
    }
  };

  const onEnable = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (_) {}
    }
    const next = { ...prefs, systemPush: true };
    setPrefs(next);
    await persist(next);
  };

  return (
    <div className="mns-page">
      <div className="mns-header">
        <button type="button" className="mns-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mns-title">Notification settings</h1>
      </div>
      <div className="mns-topband" style={{ background: ui.hero?.topBannerColor || '#fcece0' }} />
      <div className="mns-hero">
        <h2 className="mns-hero-h">{ui.hero?.heading || 'Push notifications'}</h2>
        <p className="mns-hero-p">{ui.hero?.description || ''}</p>
        <button
          type="button"
          className="mns-enable"
          onClick={onEnable}
        >
          {ui.hero?.enableCta || 'Enable'}
        </button>
      </div>
      <div className="mns-channels">
        {ui.channels.map((ch) => (
          <button
            key={ch.id}
            type="button"
            className="mns-ch-row"
            onClick={() => navigate(`/mobile/notification-settings/${ch.value || ch.id}`)}
          >
            <span className="mns-ch-lab">{ch.label}</span>
            <span className="mns-chev">›</span>
          </button>
        ))}
      </div>
    </div>
  );
}
